import {
  Grid,
  GridCellProps,
  GridNoRecords,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridSelectionChangeEvent
} from '@progress/kendo-react-grid';
import { useSessionStorage } from 'usehooks-ts';
import { Button } from '@progress/kendo-react-buttons';
import { process, State } from '@progress/kendo-data-query';
import React, { useContext, useEffect, useState } from 'react';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import Toolbar from '../toolbar';
import {
  ExportItems,
  CopyItemAsync,
  DeleteItemAsync,
  GetResourceValue,
  ImportItemsAsync,
  UpdateGameDocState
} from '../../../utils/game-document';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../components/grid/grid-tool-bar';
import { LinkCell } from '../../../components/grid';
import { getCurrentTimeStamp } from '../../../utils/date';
import { useToast } from '../../../contexts/toast-context';
import { useBreadcrumb } from '../../../contexts/breadcrumb';
import { NoRecords } from '../../../components/grid/no-records';
import { Item } from '../../../types/game-document/entities/item';
import YesNoDialog from '../../../components/dialogs/yes-no-dialog';
import { downloadJSON, handleFileUpload } from '../../../utils/files';
import { GameDocumentContext } from '../../../contexts/game-document';
import { DefaultGridSettings } from '../../../constants/grid-settings';
import EditDeleteCloneCell from '../../../components/grid/edit-delete-clone-cell';
import { ExportedItemData } from '../../../types/game-document/entities/exported-item';

const initialDataState: State = {
  sort: [{ field: 'title', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

interface ItemGrid extends Item {
  selected: boolean;
}

const SELECTED_FIELD = 'selected';

const Inventory = () => {
  const [state, setState] = useContext(GameDocumentContext);
  const [dataState, setDataState] = useSessionStorage<State>(
    'datastate-inventory',
    initialDataState
  );

  const navigate = useNavigate();
  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  const [items, setItems] = useState<ItemGrid[] | undefined>([]);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [showConfirmationDelete, setShowConfirmationDelete] = useState('');
  const { onToastOpen } = useToast();

  useEffect(() => {
    // Set the page title.
    document.title = `Inventory - ${state.gameDocument?.name}`;

    const items = (state.gameDocument?.assets.items ?? []).map((item) => ({
      ...item,
      title: GetResourceValue(state.gameDocument!, item.titleResId ?? '')
    }));
    setItems(items as any);
  }, [state]);

  const LinkedTitleCell = (props: GridCellProps) => (
    <LinkCell to={props.dataItem['id'].toString()} {...props}></LinkCell>
  );

  const DeleteCell = (props: GridCellProps) => (
    <EditDeleteCloneCell
      onEditClick={() => navigate(`${props.dataItem['id']}`)}
      onCloneClick={() => onCloneEntity(props.dataItem['id'])}
      onDeleteClick={() => setShowConfirmationDelete(props.dataItem['id'])}
      {...props}></EditDeleteCloneCell>
  );

  const handleJsonExport = async () => {
    if (selectedItemIds.length === 0) {
      onToastOpen({
        title: 'Error',
        body: 'Please select the inventory you want to export first.',
        style: 'error'
      });

      return;
    }
    const exportedItems = ExportItems(state.gameDocument!, selectedItemIds);
    const formattedTimestamp = getCurrentTimeStamp();

    downloadJSON(exportedItems, `inventory-${formattedTimestamp}.json`);
  };

  const handleJsonImport = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleFileUpload<ExportedItemData>(
      event,
      async (exportedItem) => {
        ImportItemsAsync(state.gameDocument!, exportedItem).then(
          (updatedGameDocument) => {
            setState((state) => UpdateGameDocState(state, updatedGameDocument));
            onToastOpen({
              title: 'Info',
              body: 'Import Success',
              style: 'info'
            });
          }
        );
      },
      (message) => {
        onToastOpen({
          title: 'Error',
          body: message,
          style: 'error'
        });
      }
    );
  };

  const onCloneEntity = (entityId: string) => {
    CopyItemAsync(state.gameDocument!, entityId).then((updatedGameDocument) => {
      setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
    });
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteItemAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
        setShowConfirmationDelete('');
      }
    );
  };

  const onSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem) return;
    const selectedItemId = event.dataItem['id'];

    const updatedItems = items?.map((item) =>
      item.id === selectedItemId ? { ...item, selected: !item.selected } : item
    );
    setItems(updatedItems);

    const item = updatedItems?.find((item) => item.id === selectedItemId);

    if (item) {
      const updatedSelectedItemIds = item.selected
        ? [...selectedItemIds, selectedItemId]
        : selectedItemIds.filter((id) => id !== selectedItemId);

      setSelectedItemIds(updatedSelectedItemIds);
    }
  };

  const onHeaderSelectionChange = () => {
    const updatedItems = items?.map((item) => ({
      ...item,
      selected: !item.selected
    }));

    const updatedSelectedTaskContentIds = updatedItems?.reduce<string[]>(
      (ids, item) => {
        if (item.selected) {
          return [...ids, item.id];
        } else {
          return ids.filter((id) => id !== item.id);
        }
      },
      [...(selectedItemIds || [])]
    );

    setSelectedItemIds(updatedSelectedTaskContentIds || []);
    setItems(updatedItems);
  };

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/inventory/*', text: 'Inventory' }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }

    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar
        title={'Inventory'}
        buttonHelpSupport={{
          title: 'Inventory guide',
          url: 'https://forum.catalystglobal.com/t/4593'
        }}>
        <Button onClick={() => navigate('new')} themeColor={'primary'}>
          Add item
        </Button>
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search items'}
          columnsToSearch={['title', 'description']}
          showCardMode={false}
          exportRef={gridExportRef}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          showJsonExport={true}
          handleJsonExport={handleJsonExport}
          showJsonImport={true}
          handleJsonImport={handleJsonImport}
        />
        <ExcelExport
          data={items ?? []}
          ref={gridExportRef}
          fileName={`${state.gameDocument?.name} inventory.xlsx`}>
          <Grid
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            className={'cg-grid3'}
            data={process(items ?? [], dataState)}
            dataItemKey={'id'}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: 'multiple'
            }}
            select={(items ?? []).reduce(
              (acc, item) => ({ ...acc, [item.id]: item.selected }),
              {} as { [id: string]: boolean }
            )}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
            onSelectionChange={onSelectionChange}
            onHeaderSelectionChange={onHeaderSelectionChange}>
            <Column
              filterable={false}
              field={SELECTED_FIELD}
              width={50}
              columnType="checkbox"
              headerSelectionValue={
                (items?.length ?? 0) > 0 &&
                items!.every((item) => item.selected)
              }
            />
            <Column
              field={'title'}
              title={'Title'}
              cells={{ data: LinkedTitleCell }}
            />
            <Column field={'description'} title={'Description'} />
            <Column cells={{ data: DeleteCell }} />
            <GridNoRecords>
              <NoRecords />
            </GridNoRecords>
          </Grid>
        </ExcelExport>
      </div>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(showConfirmationDelete)
              : setShowConfirmationDelete('')
          }
          onClose={() => setShowConfirmationDelete('')}>
          Are you sure you want to remove the inventory?
        </YesNoDialog>
      )}
    </>
  );
};

export default Inventory;
